import React from "react";
import { currencyCodeToSymbolMap } from "../apiHelper";

export function RfqDetails({ rfq, headerImageUrl, onBack }) {
    if (!rfq) return null;

    // Determine primary currency code and symbol from the items array
    const primaryCurrencyCode = rfq.items?.[0]?.currency || "";
    const currencySymbol = currencyCodeToSymbolMap[primaryCurrencyCode] || primaryCurrencyCode || "$";

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Print-specific styles to foce clean A4 margins/hiding */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .print-container, .print-container * {
                        visibility: visible;
                    }
                    .print-container {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        margin: 0;
                        padding: 20px !important;
                        border: none !important;
                        box-shadow: none !important;
                    }
                    @page {
                        size: A4;
                        margin: 10mm;
                    }
                }
            `}} />

            {/* Action Header - Hidden when printing */}
            <div className="print:hidden flex items-center justify-between bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition"
                >
                    ← Back to List
                </button>
                <button
                    onClick={handlePrint}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium shadow-sm transition"
                >
                    Print RFQ
                </button>
            </div>

            {/* Document Body */}
            <div className="print-container bg-white p-8 md:p-12 rounded-2xl border border-slate-100 shadow-sm">
                {headerImageUrl && (
                    <div className="mb-6 border-b border-slate-100 pb-4">
                        <img
                            src={headerImageUrl}
                            alt="Business Header"
                            className="max-h-24 object-contain"
                        />
                    </div>
                )}
                <div className="flex justify-between items-start border-b border-slate-100 pb-8">
                    <div>
                        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Request For Quote</h1>
                        <p className="text-sm font-medium text-indigo-600 mt-1">#{rfq.rfq_id} — {rfq.name}</p>
                    </div>
                    <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${rfq.accesstype_code === 1 ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            }`}>
                            {rfq.access_type} Access
                        </span>
                        <p className="text-xs text-slate-400 mt-2">
                            Date: {rfq.created_on ? new Date(rfq.created_on).toLocaleDateString() : "N/A"}
                        </p>
                    </div>
                </div>

                {/* Metadata grid */}
                <div className="grid grid-cols-2 gap-8 my-8 text-sm">
                    <div>
                        <h3 className="text-xs font-semibold uppercase text-slate-400 tracking-wider mb-2">Customer Details</h3>
                        <p className="font-semibold text-slate-800 text-base">{rfq.customer_name}</p>
                    </div>
                    <div>
                        <h3 className="text-xs font-semibold uppercase text-slate-400 tracking-wider mb-2">Created By</h3>
                        <p className="font-semibold text-slate-800 text-base">{rfq.creator_name}</p>
                    </div>
                </div>

                {/* Items Table */}
                <div className="mt-8">
                    <h3 className="text-xs font-semibold uppercase text-slate-400 tracking-wider mb-4">Requested Line Items</h3>
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase font-semibold">
                                <th className="py-3">Product Item</th>
                                <th className="py-3 text-right">Qty</th>
                                <th className="py-3 text-right">Unit Price</th>
                                <th className="py-3 text-right">Subtotal</th>
                                <th className="py-3 text-right">Tax</th>
                                <th className="py-3 text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rfq.items?.map((item, idx) => (
                                <tr key={item.rfqitem_id || idx}>
                                    <td className="py-4 font-medium text-slate-800">{item.product_name}</td>
                                    <td className="py-4 text-right text-slate-600">
                                        {item.quantity} <span className="text-xs text-slate-400">{item.qtyunit_name}</span>
                                    </td>
                                    <td className="py-4 text-right text-slate-600">{item.currency} {item.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="py-4 text-right text-slate-600">{item.currency} {item.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="py-4 text-right text-slate-600">{item.currency} {item.tax_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="py-4 text-right font-semibold text-slate-900">{item.currency} {item.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Totals Summary */}
                <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
                    <div className="w-64 space-y-2 text-sm">
                        <div className="flex justify-between text-slate-500">
                            <span>Subtotal</span>
                            <span className="font-medium text-slate-700">
                                {currencySymbol}{rfq.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                        <div className="flex justify-between text-slate-500">
                            <span>Tax</span>
                            <span className="font-medium text-slate-700">
                                {currencySymbol}{rfq.tax_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                        <div className="flex justify-between text-base font-bold text-slate-900 pt-2 border-t border-slate-200">
                            <span>Grand Total</span>
                            <span className="text-indigo-600">
                                {currencySymbol}{rfq.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}